/**
 * `RequireAuth` — the route-level auth guard for the polished app surface. The login is
 * its own `/login` route, so a guarded route **redirects** an unauthenticated visitor there.
 *
 * Four states off the one app-wide session ({@link GameSessionProvider} owns it):
 *  - **authenticated** → render the protected `children`.
 *  - **link down** (`session.linkDown`: welcomed earlier in this page, now reclaiming after a
 *    transport close) → keep rendering `children`. A wifi blip must not unmount the surface:
 *    the desktop's windows and drafts, and COMBAT.app's Pixi renderer and WebGL context, stay
 *    as they were, and the app-wide link pill ({@link LinkPill}) says why nothing syncs. Identity
 *    (`actorId`, `token`, `role`) is unchanged through a drop, so the children's reads stay valid.
 *  - **first-load reclaim** (`reclaiming` / `connecting` with no welcome yet in this page) →
 *    hold here, **NOT a redirect**. On a refresh `useGameSession` starts at `idle` while it
 *    fetches the slot list and re-presents the remembered token; without holding we'd bounce to
 *    `/login` for that beat before the socket reauthenticates. A warm reclaim is a blink, so we
 *    hold a neutral background and only surface the full-screen "Relinking…" card if it crosses
 *    ~300ms. This is the ONLY place that card appears; a transport drop never reaches it.
 *  - **otherwise** (logged out, rejected, kicked) → redirect to `/login`.
 *
 * The {@link LinkHealthProvider} and the one {@link LinkPill} mount here so they cover every
 * guarded surface, desktop and combat alike.
 */
import type { ReactNode } from "react";
import { Navigate } from "@tanstack/react-router";
import { GmsBootHold, GmsBootScreen } from "../../routes/home/GmsBootScreen";
import { LinkHealthProvider, LinkPill } from "../linkHealth";
import { useDelayedFlag } from "../useDelayedFlag";
import { useGameSessionContext } from "./GameSessionProvider";

export function RequireAuth({ children }: { children: ReactNode }) {
  const session = useGameSessionContext();
  const reconnecting = session.reclaiming || session.status === "connecting";
  const showReconnecting = useDelayedFlag(reconnecting, 300);

  if ((session.status === "authenticated" || session.linkDown) && session.actorId !== null) {
    return (
      <LinkHealthProvider>
        {children}
        <LinkPill />
      </LinkHealthProvider>
    );
  }

  if (reconnecting) {
    if (!showReconnecting) return <GmsBootHold />;
    return <GmsBootScreen status="RELINK" title="Relinking to the omninet…" detail="Restoring your link." />;
  }

  return <Navigate to="/login" />;
}
